import React from 'react';
import { Lock, Crown, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';

/**
 * Overlay component for locked modules.
 * Shows a padlock icon and upgrade options when a module is not in the user's active subscription.
 */
const LockedModuleOverlay = ({ 
  moduleName, 
  moduleId,
  description,
  showPricing = true,
  className = ''
}) => {
  const { getPricing, getUpgradeOptions, trialStatus, isOnTrial, trialDaysRemaining } = useSubscription();
  const navigate = useNavigate();
  
  const pricing = getPricing();
  const upgradeOptions = getUpgradeOptions();
  
  // Get module-specific pricing
  const plan = pricing?.plan;
  
  const handleUpgrade = () => {
    navigate(`/subscription/upgrade${moduleId ? `?module=${moduleId}` : ''}`);
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm ${className}`}>
      <Card className="max-w-lg w-full mx-4 p-8 bg-slate-900 border-slate-700 text-center">
        {/* Lock Icon */}
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-500/20 to-amber-600/20 flex items-center justify-center">
          <Lock className="w-10 h-10 text-amber-400" />
        </div>
        
        {/* Module Name */}
        <h2 className="text-2xl font-bold text-white mb-2">
          {moduleName} is Locked
        </h2>
        
        {description && (
          <p className="text-slate-400 mb-6">
            {description}
          </p>
        )}
        
        {/* Trial Status Banner */}
        {isOnTrial && trialDaysRemaining > 0 && (
          <div className="mb-6 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-400">
              Your trial includes limited modules. <strong>{trialDaysRemaining} days</strong> remaining.
            </p>
          </div>
        )}
        
        {/* Pricing Options */}
        {showPricing && (
          <div className="space-y-4 mb-6">
            {plan && (
              <div className="p-4 bg-gradient-to-r from-emerald-500/10 to-blue-500/10 rounded-lg border border-emerald-500/30" data-testid="locked-plan-price">
                <div className="flex justify-between items-center">
                  <div className="text-left">
                    <p className="text-white font-semibold">Simplif-i full platform</p>
                    <p className="text-xs text-slate-400">Every module, including {moduleName}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-emerald-400">
                      {plan.is_founding && <s className="text-sm text-slate-500 mr-1">£{plan.monthly_usual}</s>}
                      £{plan.monthly}<span className="text-sm text-slate-400">/mo</span>
                    </p>
                    <p className="text-xs text-green-400">or £{plan.annual.toLocaleString('en-GB')}/year (2 months free)</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* CTA Buttons */}
        <div className="flex flex-col gap-3">
          <Button 
            onClick={handleUpgrade}
            className="w-full bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600"
            data-testid="upgrade-module-btn"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade Now
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-slate-400 hover:text-white"
          >
            Return to Dashboard
          </Button>
        </div>
        
        {/* Additional Users Info */}
        {pricing?.additional_users && (
          <p className="mt-4 text-xs text-slate-500">
            Need more users? Add 5 users for £{pricing.additional_users[moduleId] || 15}/month
          </p>
        )}
      </Card>
    </div>
  );
};

/**
 * Inline locked module badge (for navigation items)
 */
export const LockedBadge = ({ size = 'sm' }) => {
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };
  
  return (
    <div className="ml-2 p-1 bg-amber-500/20 rounded">
      <Lock className={`${sizeClasses[size]} text-amber-400`} />
    </div>
  );
};

/**
 * Trial banner component for showing trial status
 */
export const TrialBanner = () => {
  const { isOnTrial, trialDaysRemaining, trialExpired, trialStatus } = useSubscription();
  const navigate = useNavigate();
  
  if (!isOnTrial && !trialExpired) return null;
  
  if (trialExpired) {
    return (
      <div className="bg-gradient-to-r from-red-500/10 to-amber-500/10 border-b border-red-500/30 px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-white">
              Your trial has ended. Some modules are now locked.
            </span>
          </div>
          <Button 
            size="sm" 
            onClick={() => navigate('/subscription/upgrade')}
            className="bg-gradient-to-r from-emerald-500 to-blue-500"
          >
            Upgrade Now
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-gradient-to-r from-blue-500/10 to-emerald-500/10 border-b border-blue-500/30 px-4 py-2">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-400" />
          <span className="text-sm text-white">
            Trial: <strong>{trialDaysRemaining} days</strong> remaining
            {trialStatus?.signup_source && trialStatus.signup_source !== 'grc' && (
              <span className="text-slate-400 ml-2">
                ({trialStatus.active_modules?.length || 1} module{trialStatus.active_modules?.length !== 1 ? 's' : ''} active)
              </span>
            )}
          </span>
        </div>
        <Button 
          size="sm" 
          variant="outline"
          onClick={() => navigate('/subscription/upgrade')}
          className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
        >
          View Plans
        </Button>
      </div>
    </div>
  );
};

export default LockedModuleOverlay;
